// mapLoginError — pure login-failure classifier for the Login_Page.
//
// Feature: admin-login-enablement
//
// Maps an axios-style error (or any error-like value) to a stable error kind
// and a fixed, user-facing message. The mapping is deliberately narrow so the
// Login page can decide which copy to show without ever surfacing server
// payloads, stack traces, or the entered credentials (Requirement 11.3):
//
//   HTTP 403                          -> 'invalid'    (bad credentials; email retained)
//   HTTP 500                          -> 'service'    (server error; distinct from invalid)
//   timeout (ECONNABORTED) / no response -> 'connection' (could not reach the endpoint)
//   any other status                  -> 'generic'    (catch-all failure)
//
// Requirements: 3.1, 3.2, 3.3, 3.4, 11.3

// Fixed message copy set. These are the ONLY strings this module can produce,
// which guarantees no credential, stack trace, or internal detail can leak
// through the returned message (Requirement 11.3). The `invalid` and `service`
// messages are intentionally distinct (Requirements 3.1, 3.2).
export const LOGIN_ERROR_MESSAGES = Object.freeze({
  invalid: 'Incorrect email or password. Please try again.',
  service: 'Something went wrong on our end. Please try again shortly.',
  connection: 'We could not reach the server. Check your connection and try again.',
  generic: 'We could not sign you in. Please try again.',
})

/**
 * Classify a failed login attempt into a stable error kind + fixed message.
 *
 * @param {*} errorLike An axios error, a plain object, or any thrown value.
 * @returns {{ kind: 'invalid'|'service'|'connection'|'generic', message: string }}
 */
export function mapLoginError (errorLike) {
  const kind = classify(errorLike)
  return { kind, message: LOGIN_ERROR_MESSAGES[kind] }
}

// Resolve the error kind from the error-like value without trusting any of its
// message/detail fields.
function classify (errorLike) {
  const err = errorLike || {}

  // A timeout is reported by axios via `code === 'ECONNABORTED'` regardless of
  // whether a response object exists.
  if (err.code === 'ECONNABORTED') {
    return 'connection'
  }

  const status = readStatus(err)

  // No HTTP status means the request never received a response (network error
  // or a request that failed before reaching the endpoint).
  if (status === null) {
    return 'connection'
  }

  if (status === 403) {
    return 'invalid'
  }
  if (status === 500) {
    return 'service'
  }

  // Any other received status is treated as a generic failure.
  return 'generic'
}

// Extract an HTTP status code (a number) from an axios-style error, or null
// when no response/status is present.
function readStatus (err) {
  const status = err.response && err.response.status
  return typeof status === 'number' ? status : null
}

export default mapLoginError
